import { Box, FormHelperText, TextField, Tooltip } from "@mui/material";
import strings from "global/constants/StringConstants";
import { capitalizeText, isTruthy } from "helpers/methods";
import { FileField, RequestFile, RequestParticipant } from "models/interfaces";
import { useRef, useState, createRef, useEffect } from "react";
import { useAppSelector } from "redux/hooks";
import { fieldColorFor } from "screens/SignRequest/SignRequestHelper";
import { RootState } from "utils/store";
import {
  customFieldValueError,
  emitFieldCommit,
  isLibraryField,
  legacyInlinePatternError,
} from "../ParticipantScreenService";

const CustomTextFieldsElement = ({
  field,
  dispatch,
  selectedFile,
  signer,
  currentReviewer,
  currentFieldOnFocus,
  setCurrentFieldOnFocus,
  moveToFieldLocation,
  signingJourneyOn,
  coords,
  disableFields,
}: {
  field: FileField;
  dispatch: Function;
  selectedFile: RequestFile;
  signer: RequestParticipant;
  currentReviewer: RequestParticipant;
  currentFieldOnFocus: number;
  setCurrentFieldOnFocus: Function;
  moveToFieldLocation: Function;
  signingJourneyOn: boolean;
  coords?: any;
  disableFields?: boolean;
}) => {
  const inputPadding = 0;
  const pageDims = selectedFile?.dimensions?.[field.page];
  const ry = pageDims?.y ? coords.height / pageDims.y : 1;
  let { selectedParticipant } = useAppSelector((state: RootState) => state.app);
  const fieldRefs = useRef<{
    [key: number]: any;
  }>({});
  const [error, setError] = useState<string>("");

  if (!fieldRefs.current[field.id]) {
    fieldRefs.current[field.id] = createRef();
  }

  // Captures field value at focus time so we can detect whether the user
  // actually changed anything during this focus session before counting it
  // as a commit on blur.
  const focusValueRef = useRef<string>("");

  useEffect(() => {
    Object.keys(fieldRefs.current).forEach((key) => {
      const input = fieldRefs.current[Number(key)]?.current;
      if (input) {
        if (Number(key) === currentFieldOnFocus) {
          input.focus();
          input.setSelectionRange(0, 0);
        } else {
          input.blur(); // Blur all fields that are not in focus
        }
      }
    });
    validateField(field.value || "");
  }, [currentFieldOnFocus]);

  const setInitialValue = () => {
    try {
      if (field.value && field.value.trim() !== "") {
        return; // Field already has a value, don't override it
      }

      switch (field.type) {
        case "name":
          dispatch({
            type: "UPDATE_COMMON_FIELD",
            fieldType: "name",
            value: signer.name,
          });
          return;

        case "email":
          return dispatch({
            type: "UPDATE_COMMON_FIELD",
            fieldType: "email",
            value: signer.email,
          });

        case "company_name":
          return dispatch({
            type: "UPDATE_COMMON_FIELD",
            fieldType: "company_name",
            value: selectedParticipant.companyName || "",
          });

        case "title":
          return dispatch({
            type: "UPDATE_COMMON_FIELD",
            fieldType: "title",
            value: selectedParticipant.title || "",
          });

        case "text":
        default:
          return;
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setError("");
    validateField(e.target.value);
    const normalizedType = field.type.replace("_", " ");
    if (
      [strings.companyName, strings.title, strings.name, strings.email].includes(
        normalizedType
      )
    ) {
      dispatch({
        type: "UPDATE_COMMON_FIELD",
        fieldType: field.type,
        value: e.target.value,
      });
    } else {
      dispatch({
        type: "UPDATE_FIELD",
        fileId: selectedFile.unsignedPdf || selectedFile.unsignedDoc,
        fieldId: field.name,
        value: e.target.value,
      });
    }
  };

  const validateField = (val: string) => {
    // A Fields-panel field keeps the original inline check; only a library field
    // takes the sender's extra rules (min length, options, custom regex).
    const reason = isLibraryField(field)
      ? customFieldValueError(field, val)
      : legacyInlinePatternError(field, val);
    if (reason) {
      setError(reason);
      return false;
    }
    return true;
  };

  const getType = (type: string) => {
    if (type.includes("_")) {
      type = type.replace("_", " ");
    }
    return capitalizeText(type);
  };

  const styleSize = field.styles?.size ?? 16;
  const minTextHeight =
    Math.min(coords.pageHeight - 1, styleSize * ry) || styleSize * ry;

  return (
    <Tooltip
      describeChild
      title={
        !disableFields
          ? ""
          : field.type === strings.email
            ? signer.email
            : field.toolTip
      }
      placement="top"
      arrow
    >
      <Box
        sx={{
          position: "relative",
          backgroundColor: !disableFields
            ? field.required
              ? `${fieldColorFor(field.signerIndex)}17`
              : "transparent"
            : isTruthy(currentReviewer.email)
              ? `${fieldColorFor(field.signerIndex)}17`
              : field.required
                ? "rgb(252, 224, 224)"
                : "transparent",
          height: coords.pageHeight - 2,
          width: isTruthy(coords) ? coords.pageWidth : field.w,
          border: disableFields
            ? error
              ? "1.5px dashed red"
              : currentFieldOnFocus === field.id
                ? "1.5px solid red"
                : `1.5px dashed ${fieldColorFor(field.signerIndex)}`
            : `1px solid ${fieldColorFor(field.signerIndex)}`,
          borderRadius: "6px",
          boxShadow:
            disableFields && currentFieldOnFocus === field.id && !error
              ? "0 0 0 3px rgba(255, 0, 0, 0.10)"
              : "none",
          transition: "box-shadow 0.15s ease, border-color 0.15s ease",
          padding: "0px",
        }}
      >
        <textarea
          required={field.required}
          disabled={
            field.type === strings.email ||
            field.type === "date_signed" ||
            field.readOnly ||
            isTruthy(currentReviewer.email) ||
            (!disableFields && field.type === "title") ||
            (!disableFields && field.type === "company_name") ||
            (!disableFields && field.type === "name") ||
            (!disableFields && field.type === "text")
          }
          ref={fieldRefs.current[field.id]}
          maxLength={field.maxLength || 200}
          onKeyDown={(e: any) => {
            if (e.key === "Enter" && !field.multiLine) {
              e.preventDefault();
              if (!e.shiftKey && signingJourneyOn) {
                moveToFieldLocation(field.id + 1, true);
              }
            }
          }}
          style={{
            margin: "0px",
            height: coords.pageHeight - 1,
            width: isTruthy(coords) ? coords.pageWidth : field.w,
            fontSize: minTextHeight,
            fontFamily: field.styles?.fontFamily,
            color: field.styles?.color,
            fontWeight: field.styles?.fontWeight?.bold ? "bold" : "unset",
            fontStyle: field.styles?.fontWeight?.italic ? "italic" : "unset",
            textDecoration: field.styles?.fontWeight?.underline
              ? "underline"
              : "none",
            border: "none",
            // The focused field is already indicated by the container's red
            // border + glow, so drop the browser's default (black) focus ring.
            outline: "none",
            resize: "none",
            boxSizing: "border-box",
            padding: inputPadding,
            background: "none",
            top: "0px",
            left: "2px",
            overflow: "hidden",
            position: "absolute",
          }}
          rows={1}
          placeholder={
            field.type === "text" && field.placeholder
              ? field.placeholder
              : getType(field.type)
          }
          aria-label={
            field.type === "text" && field.placeholder
              ? field.placeholder
              : getType(field.type)
          }
          aria-required={field.required}
          aria-invalid={!!error}
          aria-describedby={
            error && disableFields ? `field-error-${field.id}` : undefined
          }
          onChange={handleOnChange}
          onFocus={(e) => {
            e.stopPropagation();
            focusValueRef.current = field.value || "";
            setInitialValue();
            setCurrentFieldOnFocus(field.id);
          }}
          onBlur={() => {
            if ((field.value || "") !== focusValueRef.current) {
              emitFieldCommit();
            }
          }}
          value={field.value}
          id={`readOnly-input-field-${field.id}`}
        />
        {field.validationPattern && field.validationPattern !== "none" && disableFields && (
          <Box
            sx={{
              position: "absolute",
              top: "-18px",
              left: 0,
              fontSize: "9px",
              fontWeight: 600,
              color: error ? "#d32f2f" : fieldColorFor(field.signerIndex),
              backgroundColor: error ? "#fff0f0" : "#fff",
              padding: "1px 6px",
              zIndex: 1001,
              pointerEvents: "none",
              borderRadius: "4px 4px 0 0",
              lineHeight: 1.6,
              letterSpacing: "0.03em",
              whiteSpace: "nowrap",
              border: error
                ? "1px solid #f5c2c2"
                : `1px solid ${fieldColorFor(field.signerIndex)}55`,
              borderBottom: "none",
            }}
          >
            {field.customValidation ? "Custom" : field.validationPattern}
          </Box>
        )}
        {error && disableFields && (
          <Box
            id={`field-error-${field.id}`}
            role="alert"
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              color: "signer.danger.main", // "red" is 4:1 on white (WCAG 1.4.3)
              fontSize: "10px",
              fontWeight: 500,
              whiteSpace: "nowrap",
              backgroundColor: "rgba(255,255,255,0.95)",
              padding: "1px 4px",
              zIndex: 1000,
              pointerEvents: "none",
              borderRadius: "2px",
              border: "1px solid rgba(255,0,0,0.25)",
              lineHeight: 1.4,
            }}
          >
            {error}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default CustomTextFieldsElement;
